import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { customerSpdSelectors } from '@makemydeal/dr-activities-customer-card';
import type { CustomerCardHostState } from '@makemydeal/dr-activities-customer-card';
import { isFeatureVisible, useCustomerSelector, useDealerDetailsSelector } from '@makemydeal/dr-sales-checkin-store';
import { isValidCustomer } from '@makemydeal/dr-sales-checkin-ui-utils';
import { FEATURES } from '@makemydeal/dr-sales-checkin-types';
import type { CustomerInformationCardRef } from '../types';

/**
 * useDealSetup — Orchestration hook for the Deal Setup screen.
 *
 * Handles:
 *  - Reporting whether dealer config has landed yet (readiness)
 *  - Deciding whether Deal Setup is required before a deal can launch
 *
 * The dealer details are loaded by `DealSetupDealershipSection` via:
 *  - `updateDealerDetails()` for partial data
 *  - `fetchInitialConfigAction()` for full data including the resolved capability matrix
 *
 * IMPORTANT: When bootstrap loads an invalid deal, it populates dealerDetails.dnaAccountId
 * but skips fetching dealerSettings. We gate on the capabilities slice being *populated* to
 * ensure the dealer settings API call has completed — see the note on that gate below for why
 * the raw slice answers this and `isFeatureVisible` cannot.
 */
export const useDealSetup = () => {
    const dealerDetails = useDealerDetailsSelector();
    const { checkedInCustomer } = useCustomerSelector();

    const [customerInfoExpanded, setCustomerInfoExpanded] = useState(true);
    const [vehicleInfoExpanded, setVehicleInfoExpanded] = useState(true);

    const cardRef = useRef<CustomerInformationCardRef>(null);
    const [cardState, setCardState] = useState<CustomerCardHostState | null>(null);
    const hasValidatedCustomerRef = useRef(false);
    const isSpdCheckLoading = useSelector(customerSpdSelectors.getSpdCheckLoading);

    // hasDealerDetails: dealership is selected (dealerMMDId exists)
    const hasDealerDetails = !!dealerDetails?.dealerMMDId;

    // Readiness only — that the BFF has answered, never what it said (US2079533: the decision
    // below reads capabilities). Presence works as the signal because the BFF resolves every
    // edition to a full matrix, so a stamped `capabilities` is never empty; `isFeatureVisible`
    // could not serve here, being fail-closed and so identical before and after the response.
    // Note the level: the reducer flattens the dealer-settings *wrapper*, and `capabilities` is a
    // sibling of the inner `dealerSettings` object — so it lands here, not under `dealerSettings`.
    const hasDealerSettings = !!dealerDetails?.capabilities;

    // Rides the one capability the workflow rules read, so Deal Setup and the rules cannot disagree
    // about who owes a completed setup. `salesViewRedirection` is FULL only for DC Sales Desking;
    // Showroom and Sales Intake are one group, as everywhere else.
    const isDeskingEdition = useSelector(isFeatureVisible(FEATURES.SALES_VIEW_REDIRECTION));

    // Also needs a dealership — settings can arrive before dealerMMDId does.
    const isDealSetupRequired = hasDealerDetails && isDeskingEdition;

    // Dealer's zip from address — used as postal code fallback when buyer has none
    const dealerPostalCode = dealerDetails?.dealerSettings?.address?.zip || '';

    // Display name for the selected buyer (used as the Deal Setup accordion subtitle).
    const customerName = `${checkedInCustomer?.firstName || ''} ${checkedInCustomer?.lastName || ''}`.trim();

    // Whether the checked-in customer already has all required fields (name, email, postal code).
    // Used to gate the Launch Deal CTA in Standard mode when the deal already has a complete customer.
    const hasValidCustomer = !!checkedInCustomer && isValidCustomer(checkedInCustomer);

    // Showroom (traditional) launch override: the customer card is not used, but we still want a
    // postal code on the deal. Only fall back to the dealer's zip when the buyer doesn't already
    // have one, so we never overwrite a real value.
    const showroomCustomerOverride = useMemo(
        () => (!checkedInCustomer?.postalCode && dealerPostalCode ? { postalCode: dealerPostalCode } : {}),
        [checkedInCustomer, dealerPostalCode]
    );

    const isCardMounted = cardState !== null;
    const shouldValidateCustomerOnLoad = isCardMounted && !hasValidCustomer && !isSpdCheckLoading;
    useEffect(() => {
        if (!shouldValidateCustomerOnLoad || hasValidatedCustomerRef.current) {
            return;
        }
        hasValidatedCustomerRef.current = true;
        cardRef.current?.apply();
    }, [shouldValidateCustomerOnLoad]);

    const handleCardStateChange = useCallback((nextCardState: CustomerCardHostState) => {
        setCardState(nextCardState);
    }, []);

    const toggleCustomerInfo = () => setCustomerInfoExpanded((prev) => !prev);
    const toggleVehicleInfo = () => setVehicleInfoExpanded((prev) => !prev);

    const resetVehicleAndCustomerInfo = () => {
        setCustomerInfoExpanded(false);
        setVehicleInfoExpanded(false);
    };

    return {
        isDealSetupRequired,
        hasDealerDetails,
        hasDealerSettings,
        dealerPostalCode,
        customerName,
        hasValidCustomer,
        showroomCustomerOverride,
        customerCardControls: {
            cardRef,
            cardState,
            handleCardStateChange
        },
        sectionToggleControls: {
            toggleCustomerInfo,
            toggleVehicleInfo,
            resetVehicleAndCustomerInfo,
            customerInfoExpanded,
            vehicleInfoExpanded
        }
    };
};
